import React from 'react';
import type {
  AdminCompanyDto,
  AdminLicenseCreateRequest,
  AdminLicenseDetailDto,
  AdminLicensePatchRequest,
  AdminLicenseSaveResult,
  AdminLicenseStatus,
  AdminMutationOutcome,
} from '../types';
import {
  useLicenseDirtyClose,
  useLicenseDraftSession,
  useLicenseMutations,
  type LicenseDraftSession,
} from '../hooks/useLicenseEditor';
import { addLicenseCalendarYear, getLicenseTypeLabel } from '../lib/adminLicenses';
import { LicenseForm, LicenseMachineList } from './LicenseDrawerFields';
import {
  AdminButton,
  ConfirmDialog,
  EntityDrawer,
  StatusPill,
} from './components';

export interface LicenseDrawerProps {
  open: boolean;
  mode: 'create' | 'edit';
  license: AdminLicenseDetailDto | null;
  companies: readonly AdminCompanyDto[];
  loading?: boolean;
  onClose: () => void;
  onCreate: (request: AdminLicenseCreateRequest) => Promise<AdminLicenseDetailDto>;
  onSave: (
    id: number,
    patch: AdminLicensePatchRequest | null,
    pendingRevokeIds: readonly number[]
  ) => Promise<AdminLicenseSaveResult>;
  onDelete: (id: number) => Promise<AdminMutationOutcome>;
}

const statusTone = (status: AdminLicenseStatus) => status === 'active'
  ? 'success' as const
  : status === 'suspended' ? 'warning' as const : 'critical' as const;

const titleCase = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

const DrawerEyebrow: React.FC<{
  mode: LicenseDrawerProps['mode'];
  companyName: string;
  session: LicenseDraftSession;
}> = ({ mode, companyName, session }) => (
  <div className="admin-license-drawer-eyebrow">
    <StatusPill tone={statusTone(session.draft.status)}>{titleCase(session.draft.status)}</StatusPill>
    <StatusPill tone="line">{getLicenseTypeLabel(session.draft.type)}</StatusPill>
    <span className="admin-license-drawer-company">
      {mode === 'create' ? 'New license' : companyName}
    </span>
  </div>
);

const DrawerActions: React.FC<{
  mode: LicenseDrawerProps['mode'];
  hasLicense: boolean;
  session: LicenseDraftSession;
  copyKey: () => Promise<void>;
}> = ({ mode, hasLicense, session, copyKey }) => (
  <>
    {mode === 'edit' && hasLicense && <AdminButton onClick={() => void copyKey()}>Copy key</AdminButton>}
    <AdminButton onClick={() => session.setField(
      'dueDate',
      addLicenseCalendarYear(session.draft.dueDate)
    )}>Extend 1 year</AdminButton>
    <AdminButton onClick={() => session.setField(
      'status',
      session.draft.status === 'active' ? 'suspended' : 'active'
    )}>{session.draft.status === 'active' ? 'Suspend' : 'Reactivate'}</AdminButton>
  </>
);

const DrawerFooter: React.FC<{
  mode: LicenseDrawerProps['mode'];
  hasLicense: boolean;
  canSubmit: boolean;
  submitting: boolean;
  cancel: () => Promise<void>;
  openDelete: () => void;
  submit: () => Promise<void>;
}> = (props) => (
  <>
    {props.mode === 'edit' && (
      <AdminButton variant="danger" disabled={!props.hasLicense || props.submitting} onClick={props.openDelete}>Delete</AdminButton>
    )}
    <span className="admin-license-drawer-footer-spacer" />
    <AdminButton variant="ghost" disabled={props.submitting} onClick={() => void props.cancel()}>Cancel</AdminButton>
    <AdminButton variant="primary" disabled={!props.canSubmit} onClick={() => void props.submit()}>
      {props.submitting ? 'Saving\u2026' : props.mode === 'create' ? 'Create license' : 'Save changes'}
    </AdminButton>
  </>
);

const DrawerBody: React.FC<{
  mode: LicenseDrawerProps['mode'];
  license: AdminLicenseDetailDto | null;
  companies: readonly AdminCompanyDto[];
  loading: boolean;
  session: LicenseDraftSession;
}> = ({ mode, license, companies, loading, session }) => {
  if (loading || (mode === 'edit' && !license)) {
    return <div className="admin-license-drawer-loading" role="status">{'Loading license details\u2026'}</div>;
  }
  return (
    <>
      {session.saveError && <div className="admin-license-form-error" role="alert">{session.saveError}</div>}
      <LicenseForm
        companies={companies}
        draft={session.draft}
        errors={session.validation}
        serverLimitError={session.serverLimitError}
        setField={session.setField}
      />
      {mode === 'edit' && license && (
        <LicenseMachineList
          license={license}
          maximum={session.draft.allowedInstances}
          pendingIds={session.pendingRevokeIds}
          toggleRevoke={session.toggleRevoke}
        />
      )}
    </>
  );
};

const LicenseDrawer: React.FC<LicenseDrawerProps> = (props) => {
  const loading = props.loading ?? false;
  const session = useLicenseDraftSession(
    props.open,
    props.mode,
    props.license,
    props.companies[0]?.id ?? 0
  );
  const mutations = useLicenseMutations({ ...props, loading, session });
  const dirtyClose = useLicenseDirtyClose({
    blocked: mutations.deleteOpen,
    dirty: session.dirty,
    onClose: props.onClose,
  });
  const company = props.companies.find((item) => item.id === session.draft.companyId);
  const title = props.mode === 'create'
    ? 'New license'
    : <span className="admin-license-drawer-key">{props.license?.activationKey ?? 'Loading license\u2026'}</span>;

  return (
    <>
      <EntityDrawer
        open={props.open}
        title={title}
        ariaLabel={props.mode === 'create' ? 'New license' : 'Edit license'}
        eyebrow={<DrawerEyebrow mode={props.mode} companyName={company?.name ?? props.license?.companyName ?? '\u2014'} session={session} />}
        actions={!loading ? <DrawerActions mode={props.mode} hasLicense={Boolean(props.license)} session={session} copyKey={mutations.copyKey} /> : undefined}
        footer={<DrawerFooter mode={props.mode} hasLicense={Boolean(props.license)} canSubmit={mutations.canSubmit} submitting={mutations.submitting} cancel={dirtyClose.requestCancel} openDelete={() => mutations.setDeleteOpen(true)} submit={mutations.submit} />}
        dirty={session.dirty}
        onDirtyClose={dirtyClose.confirmDirtyClose}
        onClose={props.onClose}
      >
        <DrawerBody mode={props.mode} license={props.license} companies={props.companies} loading={loading} session={session} />
      </EntityDrawer>
      <ConfirmDialog
        open={mutations.deleteOpen}
        title="Delete this license?"
        description={`${props.license?.activationKey ?? 'This license'} will be deleted and all ${props.license?.machinesCount ?? 0} activations revoked. This cannot be undone.`}
        confirmLabel={mutations.submitting ? 'Deleting\u2026' : 'Delete license'}
        confirmDisabled={mutations.submitting}
        closeOnConfirm={false}
        onConfirm={mutations.confirmDelete}
        onClose={() => mutations.setDeleteOpen(false)}
      />
      <ConfirmDialog
        open={dirtyClose.discardOpen}
        title="Discard unsaved changes?"
        description="Your license edits and pending revokes will be lost."
        confirmLabel="Discard changes"
        closeOnConfirm={false}
        onConfirm={() => dirtyClose.resolveDiscard(true)}
        onClose={() => dirtyClose.resolveDiscard(false)}
      />
    </>
  );
};

export default LicenseDrawer;
